/**
 * Transport for the Apply Content Language endpoints.
 *
 * Every call resolves to a result rather than throwing, and a failure is
 * reduced to a named kind here. That is the whole point of this module: the
 * store and the components decide what to do about `forbidden` or `conflict`
 * without ever seeing an axios error, a status code, or a response body, so
 * nothing downstream can accidentally render a payload the service did not
 * intend for a user to read.
 *
 * The one exception is a 409, which carries the id of the job already running
 * for the account. That id is the point of the response: it lets the Admin
 * reattach to the existing job instead of reporting a dead end.
 */
import axios from 'axios'

import type {
  LanguageJobFailure,
  LanguageJobFailureKind,
  LanguageJobResultsPage,
  LanguageJobStatus,
  StartLanguageJobResponse,
} from './languageJob'
import { getDescriptionsApi } from './utils'
import { useAccountSettingsStore } from './stores/accountSettings'

export type LanguageJobResult<T> = { ok: true; data: T } | { ok: false; failure: LanguageJobFailure }

const authHeaders = (): Record<string, string> => {
  try {
    const store = useAccountSettingsStore()
    const token = String(store?.sessionToken || '').trim()
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

const KIND_BY_STATUS: Record<number, LanguageJobFailureKind> = {
  401: 'unauthenticated',
  403: 'forbidden',
  404: 'not-found',
  409: 'conflict',
}

const readJobId = (data: unknown): string | undefined => {
  const jobId = (data as { jobId?: unknown })?.jobId
  return typeof jobId === 'string' && jobId.trim() !== '' ? jobId : undefined
}

/**
 * Reduce any thrown transport error to a named failure. Nothing from the error
 * itself survives except the status code and, for a conflict, the job id.
 */
const toFailure = (error: unknown): LanguageJobFailure => {
  const response = axios.isAxiosError(error) ? error.response : undefined
  const kind = KIND_BY_STATUS[response?.status ?? 0] ?? 'unavailable'
  return kind === 'conflict' ? { kind, jobId: readJobId(response?.data) } : { kind }
}

const endpointFor = (path: string): string => `${getDescriptionsApi()}/${path}`

export const startLanguageJob = async (
  accountId: string,
): Promise<LanguageJobResult<StartLanguageJobResponse>> => {
  try {
    const { data } = await axios.post<StartLanguageJobResponse>(
      endpointFor('language-jobs'),
      { accountId },
      { headers: { ...authHeaders() } },
    )
    return { ok: true, data }
  } catch (error) {
    return { ok: false, failure: toFailure(error) }
  }
}

export const fetchLanguageJobStatus = async (
  accountId: string,
  jobId: string,
): Promise<LanguageJobResult<LanguageJobStatus>> => {
  try {
    const { data } = await axios.get<LanguageJobStatus>(
      endpointFor(`language-jobs/${encodeURIComponent(jobId)}`),
      { params: { accountId }, headers: { ...authHeaders() } },
    )
    return { ok: true, data }
  } catch (error) {
    return { ok: false, failure: toFailure(error) }
  }
}

export const fetchLanguageJobResults = async (
  accountId: string,
  jobId: string,
  page: number,
): Promise<LanguageJobResult<LanguageJobResultsPage>> => {
  try {
    const { data } = await axios.get<LanguageJobResultsPage>(
      endpointFor(`language-jobs/${encodeURIComponent(jobId)}/results`),
      { params: { accountId, page }, headers: { ...authHeaders() } },
    )
    return { ok: true, data }
  } catch (error) {
    return { ok: false, failure: toFailure(error) }
  }
}
